import { getChatCompletion } from './aiIntegrations/chatCompletion';

const AR_BEHAVIORAL_SYSTEM_PROMPT = `You are a real-time financial behavioral advisor for AR purchase decisions. Analyze the purchase context and provide:
1. Impulse Risk Assessment: Evaluate likelihood of impulse purchase
2. Smart Nudges: Provide non-intrusive behavioral guidance
3. Alternative Suggestions: Recommend lower-cost or better-timed alternatives
4. Wait Recommendation: Suggest if user should delay purchase

Format your response as JSON with this structure:
{
  "impulseRisk": {"score": 0-100, "level": "low|medium|high", "reasoning": "string"},
  "nudges": [{"message": "string", "type": "warning|info|success"}],
  "alternatives": [{"suggestion": "string", "savings": "string"}],
  "waitRecommendation": {"shouldWait": boolean, "reason": "string", "suggestedDate": "string"}
}`;

class ARFinancialService {
  constructor() {
    this.budgetData = null;
    this.spendingHistory = [];
  }

  setBudgetData(budgetCategories) {
    this.budgetData = budgetCategories;
  }

  setSpendingHistory(history) {
    this.spendingHistory = history;
  }

  calculatePurchaseImpact(productCost, category) {
    if (!this.budgetData) {
      return this.getDefaultImpact(productCost);
    }

    // Find matching budget category
    const budgetCategory = this.budgetData?.find(
      cat => cat?.name?.toLowerCase() === category?.toLowerCase()
    );

    if (!budgetCategory) {
      return this.getDefaultImpact(productCost);
    }

    const currentSpent = budgetCategory?.spent || 0;
    const budget = budgetCategory?.budget || 0;
    const remaining = budget - currentSpent;
    const newSpent = currentSpent + productCost;
    const newPercentage = (newSpent / budget) * 100;

    // Calculate risk level
    let riskLevel = 'safe';
    let riskColor = '#10b981'; // green
    if (newPercentage >= 100) {
      riskLevel = 'critical';
      riskColor = '#ef4444'; // red
    } else if (newPercentage >= 90) {
      riskLevel = 'high';
      riskColor = '#f59e0b'; // yellow
    } else if (newPercentage >= 75) {
      riskLevel = 'caution';
      riskColor = '#f59e0b'; // yellow
    }

    // Calculate monthly budget deviation
    const monthlyDeviation = ((newSpent - budget) / budget) * 100;

    // Calculate safe-to-spend balance
    const daysInMonth = 30;
    const dailySafeLimit = remaining / daysInMonth;
    const safeToSpend = Math.max(0, remaining - productCost);

    return {
      estimatedCost: productCost,
      category: category,
      categoryBudget: budget,
      currentSpent: currentSpent,
      remainingBudget: remaining,
      afterPurchaseRemaining: remaining - productCost,
      budgetPercentage: newPercentage,
      monthlyDeviation: monthlyDeviation,
      safeToSpend: safeToSpend,
      dailySafeLimit: dailySafeLimit,
      riskLevel: riskLevel,
      riskColor: riskColor,
      canAfford: remaining >= productCost,
      impactOnSavings: this.calculateSavingsImpact(productCost),
      financialHealthImpact: this.calculateHealthScoreImpact(productCost, newPercentage)
    };
  }

  getDefaultImpact(productCost) {
    return {
      estimatedCost: productCost,
      category: 'Unknown',
      categoryBudget: 0,
      currentSpent: 0,
      remainingBudget: 0,
      afterPurchaseRemaining: 0,
      budgetPercentage: 0,
      monthlyDeviation: 0,
      safeToSpend: 0,
      dailySafeLimit: 0,
      riskLevel: 'unknown',
      riskColor: '#6b7280',
      canAfford: false,
      impactOnSavings: { delay: 0, impact: 'Unknown' },
      financialHealthImpact: { scoreChange: 0, newScore: 0 }
    };
  }

  calculateSavingsImpact(purchaseCost) {
    // Assume 20% savings rate
    const monthlySavings = 1000; // Default
    const savingsDelay = (purchaseCost / monthlySavings)?.toFixed(1);
    
    let impact = 'Minimal';
    if (savingsDelay > 3) impact = 'Significant';
    else if (savingsDelay > 1) impact = 'Moderate';

    return {
      delay: savingsDelay,
      impact: impact,
      message: `Delays savings goal by ${savingsDelay} months`
    };
  }

  calculateHealthScoreImpact(purchaseCost, newBudgetPercentage) {
    let scoreChange = 0;
    
    if (newBudgetPercentage >= 100) scoreChange = -8;
    else if (newBudgetPercentage >= 90) scoreChange = -5;
    else if (newBudgetPercentage >= 75) scoreChange = -3;
    else if (newBudgetPercentage < 50) scoreChange = 1;

    const currentScore = 78; // Default from dashboard
    const newScore = Math.max(0, Math.min(100, currentScore + scoreChange));

    return {
      scoreChange: scoreChange,
      newScore: newScore,
      impact: scoreChange < 0 ? 'Negative' : scoreChange > 0 ? 'Positive' : 'Neutral'
    };
  }

  async analyzeBehavioralTriggers(purchaseContext) {
    try {
      const userPrompt = `Analyze this purchase decision:

Product: ${purchaseContext?.productName}
Estimated Cost: $${purchaseContext?.estimatedCost}
Category: ${purchaseContext?.category}
Current Budget Status: ${purchaseContext?.budgetPercentage}% used
Remaining Budget: $${purchaseContext?.remainingBudget}
Time of Day: ${new Date()?.getHours()}:00

Recent Spending Pattern:
${this.getSpendingPatternSummary()}

Provide behavioral analysis and recommendations.`;

      const response = await getChatCompletion(
        'OPEN_AI',
        'gpt-5',
        [
          { role: 'system', content: AR_BEHAVIORAL_SYSTEM_PROMPT },
          { role: 'user', content: userPrompt }
        ],
        {
          max_completion_tokens: 1000,
          temperature: 1,
          response_format: {
            type: 'json_schema',
            json_schema: {
              name: 'ARBehavioralAnalysis',
              schema: {
                type: 'object',
                properties: {
                  impulseRisk: {
                    type: 'object',
                    properties: {
                      score: { type: 'number' },
                      level: { type: 'string', enum: ['low', 'medium', 'high'] },
                      reasoning: { type: 'string' }
                    },
                    required: ['score', 'level', 'reasoning'],
                    additionalProperties: false
                  },
                  nudges: {
                    type: 'array',
                    items: {
                      type: 'object',
                      properties: {
                        message: { type: 'string' },
                        type: { type: 'string', enum: ['warning', 'info', 'success'] }
                      },
                      required: ['message', 'type'],
                      additionalProperties: false
                    }
                  },
                  alternatives: {
                    type: 'array',
                    items: {
                      type: 'object',
                      properties: {
                        suggestion: { type: 'string' },
                        savings: { type: 'string' }
                      },
                      required: ['suggestion', 'savings'],
                      additionalProperties: false
                    }
                  },
                  waitRecommendation: {
                    type: 'object',
                    properties: {
                      shouldWait: { type: 'boolean' },
                      reason: { type: 'string' },
                      suggestedDate: { type: 'string' }
                    },
                    required: ['shouldWait', 'reason', 'suggestedDate'],
                    additionalProperties: false
                  }
                },
                required: ['impulseRisk', 'nudges', 'alternatives', 'waitRecommendation'],
                additionalProperties: false
              },
              strict: true
            }
          }
        }
      );

      const content = response?.choices?.[0]?.message?.content;
      if (!content) {
        console.warn('No content received from AI response');
        return this.getDefaultBehavioralAnalysis();
      }
      return JSON.parse(content);
    } catch (error) {
      console.error('Behavioral analysis error:', error);
      return this.getDefaultBehavioralAnalysis();
    }
  }

  getSpendingPatternSummary() {
    if (this.spendingHistory?.length === 0) {
      return 'No recent spending history available';
    }

    const recentTransactions = this.spendingHistory?.slice(0, 5);
    return recentTransactions?.map(t => `- ${t?.category}: $${t?.amount} (${t?.date})`)?.join('\n');
  }

  getDefaultBehavioralAnalysis() {
    return {
      impulseRisk: {
        score: 50,
        level: 'medium',
        reasoning: 'Unable to analyze spending patterns'
      },
      nudges: [
        {
          message: 'Consider waiting 24 hours before making this purchase',
          type: 'info'
        }
      ],
      alternatives: [],
      waitRecommendation: {
        shouldWait: true,
        reason: 'Insufficient data for immediate recommendation',
        suggestedDate: new Date(Date.now() + 86400000)?.toLocaleDateString()
      }
    };
  }

  simulateFinancialScenario(purchaseCost, category) {
    let impact = this.calculatePurchaseImpact(purchaseCost, category);
    
    // Calculate timeline projections
    const savingsGoalAmount = 10000; // Default goal
    const monthlySavingsRate = 500;
    const currentSavings = 3000;

    const monthsToGoalBefore = (savingsGoalAmount - currentSavings) / monthlySavingsRate;
    const monthsToGoalAfter = (savingsGoalAmount - (currentSavings - purchaseCost)) / monthlySavingsRate;
    const goalDelay = monthsToGoalAfter - monthsToGoalBefore;

    // Calculate cash runway
    const monthlyExpenses = 3500;
    const currentBalance = 8000;
    const cashRunwayBefore = currentBalance / monthlyExpenses;
    const cashRunwayAfter = (currentBalance - purchaseCost) / monthlyExpenses;
    const runwayReduction = cashRunwayBefore - cashRunwayAfter;

    // Calculate goal completion probability
    const baseProbability = 75;
    const probabilityAdjustment = impact?.riskLevel === 'critical' ? -15 : 
                                  impact?.riskLevel === 'high' ? -10 : 
                                  impact?.riskLevel === 'caution' ? -5 : 0;
    const newProbability = Math.max(0, Math.min(100, baseProbability + probabilityAdjustment));

    return {
      purchaseCost: purchaseCost,
      category: category,
      savingsTimeline: {
        currentMonthsToGoal: monthsToGoalBefore?.toFixed(1),
        afterPurchaseMonthsToGoal: monthsToGoalAfter?.toFixed(1),
        delayInMonths: goalDelay?.toFixed(1)
      },
      cashRunway: {
        currentDays: (cashRunwayBefore * 30)?.toFixed(0),
        afterPurchaseDays: (cashRunwayAfter * 30)?.toFixed(0),
        reductionDays: (runwayReduction * 30)?.toFixed(0)
      },
      goalProbability: {
        current: baseProbability,
        afterPurchase: newProbability,
        change: probabilityAdjustment
      },
      retirementImpact: {
        delay: (goalDelay * 0.5)?.toFixed(1),
        message: `Retirement savings delayed by ${(goalDelay * 0.5)?.toFixed(1)} months`
      }
    };
  }
}

export default ARFinancialService;
